import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * Interrupteur Terreau (`vw-switch`) : un vrai bouton `role="switch"` de 44 px
 * de haut, la piste de 44 × 26 px dessinée dedans. Vert `vw-primary` allumé,
 * `vw-line-strong` éteint ; le lecteur d'écran annonce « activé / désactivé ».
 *
 * Remplace les cases à cocher maquillées en interrupteur (préférences de
 * notifications) : la zone tactile était de 24 px et les couleurs en dur.
 *
 * Usage : `<vw-switch [(checked)]="p.isEnabled" [ariaLabel]="'…' | translate" />`
 */
@Component({
  selector: 'vw-switch',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <button type="button" role="switch" class="vw-switch flex h-target w-14 shrink-0 items-center justify-center disabled:opacity-40"
      [attr.aria-checked]="checked" [attr.aria-label]="ariaLabel || null" [disabled]="disabled" (click)="toggle()">
      <span class="relative block h-[26px] w-11 rounded-pill transition-colors"
        [class.bg-vw-primary]="checked" [class.bg-vw-line-strong]="!checked" aria-hidden="true">
        <span class="absolute left-0.5 top-0.5 block h-[22px] w-[22px] rounded-pill bg-vw-surface shadow-float transition-transform"
          [class.translate-x-[18px]]="checked"></span>
      </span>
    </button>
  `,
  styles: [
    `
      :host { display: inline-flex; }
      .vw-switch { -webkit-tap-highlight-color: transparent; }
      .vw-switch:focus-visible { outline: 2px solid var(--vw-primary); outline-offset: 2px; border-radius: var(--vw-radius-pill); }
    `,
  ],
})
export class SwitchComponent {
  @Input() checked = false;
  @Input() disabled = false;
  /** Nom lu par le lecteur d'écran, déjà traduit. */
  @Input() ariaLabel = '';
  @Output() checkedChange = new EventEmitter<boolean>();

  toggle(): void {
    if (this.disabled) return;
    this.checked = !this.checked;
    this.checkedChange.emit(this.checked);
  }
}
